import React, { createContext, useContext, useEffect, useState } from 'react';

// Déclarer l'extension de l'interface Window pour inclure notre propriété globale
declare global {
  interface Window {
    __LAYOUT_INFO__?: {
      isMobile: boolean;
      isSmallMobile: boolean;
      width: number;
      height: number;
    };
  }
}

// Type pour le contexte
interface LayoutContextType {
  isMobile: boolean;
  isSmallMobile: boolean;
  width: number;
  height: number;
}

// Fonction pour stocker la disposition dans localStorage
const saveLayoutToStorage = (layout: LayoutContextType) => {
  try {
    localStorage.setItem('layout-state', JSON.stringify({
      isMobile: layout.isMobile,
      isSmallMobile: layout.isSmallMobile,
      width: layout.width,
      height: layout.height,
      timestamp: Date.now()
    }));
  } catch (e) {
    console.error('Failed to save layout to localStorage:', e);
  }
};

// Fonction pour récupérer la disposition depuis localStorage
const getLayoutFromStorage = (): LayoutContextType | null => {
  try {
    const storedLayout = localStorage.getItem('layout-state');
    if (!storedLayout) return null;
    
    const parsedLayout = JSON.parse(storedLayout);
    const now = Date.now();
    // Si les données sont trop anciennes (plus de 1 heure), on les considère comme invalides
    if (now - parsedLayout.timestamp > 3600000) return null;
    
    return {
      isMobile: parsedLayout.isMobile,
      isSmallMobile: parsedLayout.isSmallMobile,
      width: parsedLayout.width,
      height: parsedLayout.height
    };
  } catch (e) {
    console.error('Failed to get layout from localStorage:', e);
    return null;
  }
};

// Détection initiale de la disposition (exécutée immédiatement)
const detectInitialLayout = (): LayoutContextType => {
  // Vérifier si la variable globale __LAYOUT_INFO__ existe (définie dans index.html)
  if (typeof window !== 'undefined' && window.__LAYOUT_INFO__) {
    return window.__LAYOUT_INFO__ as LayoutContextType;
  }
  
  // Essayer d'abord de récupérer depuis localStorage
  const storedLayout = getLayoutFromStorage();
  if (storedLayout) return storedLayout;
  
  // Sinon, détecter à partir de la taille de la fenêtre
  const width = typeof window !== 'undefined' ? window.innerWidth : 0;
  const height = typeof window !== 'undefined' ? window.innerHeight : 0;
  
  return {
    isMobile: width <= 768,
    isSmallMobile: width <= 480,
    width,
    height
  };
};

// Valeurs par défaut du contexte (détectées immédiatement)
const defaultContext: LayoutContextType = detectInitialLayout();

// Création du contexte
const LayoutContext = createContext<LayoutContextType>(defaultContext);

// Hook personnalisé pour utiliser le contexte
export const useLayout = () => useContext(LayoutContext);

// Provider du contexte
export const LayoutProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [layoutState, setLayoutState] = useState<LayoutContextType>(defaultContext);

  useEffect(() => {
    // Fonction pour mettre à jour l'état
    function handleResize() {
      const width = window.innerWidth;
      const height = window.innerHeight;
      const newState = {
        width,
        height,
        isMobile: width <= 768,
        isSmallMobile: width <= 480
      };
      
      setLayoutState(newState);
      saveLayoutToStorage(newState);
    }
    
    // Appeler une fois pour définir les valeurs initiales
    handleResize();
    
    // Ajouter l'écouteur d'événement
    window.addEventListener('resize', handleResize);
    
    // Nettoyage de l'écouteur d'événement lors du démontage
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Stockage des valeurs dans un attribut data- du body pour consultation facile
  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.body.dataset.isMobile = String(layoutState.isMobile);
      document.body.dataset.isSmallMobile = String(layoutState.isSmallMobile);
      document.body.dataset.width = String(layoutState.width);
    }
  }, [layoutState]);

  return (
    <LayoutContext.Provider value={layoutState}>
      {children}
    </LayoutContext.Provider>
  );
};